import { events as createPointerEvents } from '@react-three/fiber';

import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { Material, Mesh, Intersection as ThreeIntersection, Object3D } from 'three';

export function disposeObject(object: Mesh | null) {
    if (!object) {
        return;
    }

    object.geometry?.dispose();

    const materials = object.material;
    if (Array.isArray(materials)) {
        materials.forEach((material: Material | null | undefined) => material?.dispose?.());
    } else {
        materials?.dispose?.();
    }
}

export function isInputElement(element: EventTarget | null): boolean {
    return (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        (element instanceof HTMLElement && element.isContentEditable)
    );
}

export function isTextInputElement(element: EventTarget | null): boolean {
    if (element instanceof HTMLInputElement) {
        // Mantine NumberInput uses a text input with a numeric input mode.
        return (
            element.inputMode !== 'numeric' &&
            element.inputMode !== 'decimal' &&
            element.closest('[data-transformation-input]') === null &&
            ['text', 'search', 'email', 'password', 'tel', 'url'].includes(element.type)
        );
    }

    return element instanceof HTMLTextAreaElement || (element instanceof HTMLElement && element.isContentEditable);
}

export const isSceneToolActive = (
    state: Pick<PlacementState, 'arePivotControlsActive' | 'isPanelQuickReplacementOpen'>
): boolean => state.arePivotControlsActive || state.isPanelQuickReplacementOpen;

const isPivotControlIntersection = (intersection: ThreeIntersection): boolean => {
    for (let object: Object3D | null = intersection.object; object; object = object.parent) {
        if (object.userData.isPivotControl === true) {
            return true;
        }
    }

    return false;
};

const isCameraGizmoIntersection = (intersection: ThreeIntersection): boolean => {
    for (let object: Object3D | null = intersection.object; object; object = object.parent) {
        if (object.userData.isCameraGizmo === true) {
            return true;
        }
    }

    return false;
};

export const isPoiIntersection = (intersection: ThreeIntersection): boolean => {
    let isPoi = false;
    for (let object: Object3D | null = intersection.object; object; object = object.parent) {
        if (!object.visible) {
            return false;
        }
        isPoi ||= object.name === 'poi-interaction-target';
    }
    return isPoi;
};

const isSelectedInstanceIntersection = (intersection: ThreeIntersection, state: PlacementState): boolean => {
    const { object, batchId } = intersection;
    if (batchId === undefined) {
        return false;
    }
    const instanceMap =
        object === state.batchedMeshes.physical
            ? state.physicalBatchToInstance
            : object === state.batchedMeshes.sheet
              ? state.sheetBatchToInstance
              : object === state.batchedMeshes.holes
                ? state.holesBatchToInstance
                : undefined;
    const instanceId = instanceMap?.get(batchId);
    return (
        instanceId !== undefined && Object.prototype.hasOwnProperty.call(state.articleInstanceSelections, instanceId)
    );
};

export const mainCanvasPointerEvents: typeof createPointerEvents = (store) => {
    const pointerEvents = createPointerEvents(store);

    return {
        ...pointerEvents,
        filter: (intersections) => {
            const state = usePlacementStore.getState();
            const pivotControlHits: ThreeIntersection[] = [];
            const sceneHits: ThreeIntersection[] = [];
            for (const intersection of intersections) {
                if (isPivotControlIntersection(intersection)) {
                    if (!isSceneToolActive(state) || state.arePivotControlsActive) {
                        pivotControlHits.push(intersection);
                    }
                } else if (
                    !isSceneToolActive(state) ||
                    isSelectedInstanceIntersection(intersection, state) ||
                    (state.isPanelQuickReplacementOpen && isPoiIntersection(intersection)) ||
                    (state.isPanelQuickReplacementOpen && isCameraGizmoIntersection(intersection))
                ) {
                    sceneHits.push(intersection);
                }
            }

            if (state.interactionMode === InteractionMode.POI_NAVIGATION) {
                const cornerIndex = sceneHits.findIndex(
                    (hit) =>
                        isPoiIntersection(hit) &&
                        hit.batchId !== undefined &&
                        hit.object.userData.poiCornerBatchIds instanceof Set &&
                        hit.object.userData.poiCornerBatchIds.has(hit.batchId)
                );
                const firstPoiIndex = sceneHits.findIndex(isPoiIntersection);
                if (cornerIndex > firstPoiIndex && firstPoiIndex >= 0) {
                    const [cornerHit] = sceneHits.splice(cornerIndex, 1);
                    sceneHits.splice(firstPoiIndex, 0, cornerHit);
                }
            }

            return [...pivotControlHits, ...sceneHits];
        }
    };
};
